/**
 * The sound of the world (decision 0022, layer 2), worked out from what the screen shows: the
 * ambience of the ground the screen shows and the wind over it, the cabin's fire and pennant and
 * what else burns or turns near the middle of the screen, the gnats close up, and the calls and
 * splashes of what lives there, each as it moves so. Only what the scene shows counts, so the
 * sound never tells what the mist hides. Pure: `main.ts` reads the view and the scene for it and
 * plays what it says, and the app's `Ambience.swift` follows the same rules.
 */
import { familyOf, type SoundId } from "@mistsweeper/assets";

/** How often the screen is listened to, in milliseconds, and how far ahead a call is set. */
export const LISTEN_MS = 200;
export const LOOKAHEAD_S = 0.4;
/** The points of the screen whose ground is read: columns and rows, each at its cell's middle. */
export const SAMPLE_COLUMNS = 5;
export const SAMPLE_ROWS = 9;
/**
 * Far out with this many tiles across the middle of the screen or more; close up with these. A
 * run starts with about five and a half across a phone, at about two thirds of the way in.
 */
const FAR_TILES = 40;
const NEAR_TILES = 2;

/**
 * A window of the world's ground, as the renderer's scene keeps it: row by row from (x0, y0),
 * 0 where no ground shows, else the terrain plus 1, plus 8 where a number shows.
 */
export interface GroundWindow {
  readonly x0: number;
  readonly y0: number;
  readonly size: number;
  readonly ground: Uint8Array;
}

/** The terrain a tile shows, or null where the mist hides it or the window ends. */
export function shownTerrain(window: GroundWindow, x: number, y: number): number | null {
  const i = x - window.x0;
  const j = y - window.y0;
  if (i < 0 || j < 0 || i >= window.size || j >= window.size) return null;
  const g = window.ground[j * window.size + i] ?? 0;
  return g === 0 ? null : (g & 7) - 1;
}

/** The shares of the screen's points that show ground of each kind; the rest is mist. */
export interface GroundShares {
  readonly water: number;
  readonly grass: number;
  readonly forest: number;
  readonly mountain: number;
}

/** Levels from 0 to 1 by sound, before each sound's own gain. */
export type Levels = Partial<Record<SoundId, number>>;

/** A call on the clock, in seconds, and the round it belongs to, so that it sounds once. */
export interface Call {
  readonly at: number;
  readonly round: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function smoothstep(a: number, b: number, v: number): number {
  const t = clamp((v - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}

function fract(v: number): number {
  return v - Math.floor(v);
}

/**
 * How near the camera is, from 0 far out to 1 close up, by the tiles across the middle of the
 * screen: FAR_TILES and more far out, NEAR_TILES and fewer close up, on a log scale between.
 */
export function nearness(tilesAcross: number): number {
  if (!(tilesAcross > 0)) return 0;
  return clamp(Math.log(FAR_TILES / tilesAcross) / Math.log(FAR_TILES / NEAR_TILES), 0, 1);
}

/**
 * The ambience: the wind always, the more the further out, and each ground by the square root of
 * its share of the screen, fullest close up; the forest by day or by night, the meadow by day.
 */
export function bedLevels(shares: GroundShares, near: number, night: boolean): Levels {
  const ground = 0.4 + 0.6 * near;
  const of = (share: number) => Math.sqrt(clamp(share, 0, 1)) * ground;
  return {
    "ambience.wind": 0.3 + 0.7 * (1 - near),
    "ambience.forest-day": night ? 0 : of(shares.forest),
    "ambience.forest-night": night ? of(shares.forest) : 0,
    "ambience.meadow": night ? 0 : of(shares.grass),
    "ambience.water": of(shares.water),
    "ambience.fell": of(shares.mountain),
  };
}

/**
 * How near the middle of a screen `w` by `h` a point is: 1 there, 0 at `reach` times the way to
 * a corner and beyond, falling off as a square.
 */
export function proximity(x: number, y: number, w: number, h: number, reach = 1): number {
  const d = Math.hypot(x - w / 2, y - h / 2) / (0.5 * Math.hypot(w, h));
  const q = clamp(1 - d / reach, 0, 1);
  return q * q;
}

/** The cabin's fire and pennant: heard close up, the nearer the middle the louder. */
export function homeLevel(q: number, near: number): number {
  return q * smoothstep(0.25, 0.75, near);
}

/** What burns or turns, while it shows: heard from a little further out than the cabin. */
export function sourceLevel(q: number, near: number): number {
  return q * smoothstep(0.1, 0.6, near);
}

/** The families that sound while they show, by the loop each plays. */
export const SOURCES: Readonly<Record<string, SoundId>> = {
  kolmila: "ambience.kolmila",
  campfire: "ambience.campfire",
  watermill: "ambience.watermill",
};

/** What a model sounds of while it shows: a loop, or a call heard only with the world's life. */
export type Sounding =
  | { readonly loop: SoundId; readonly call?: undefined; readonly life: false }
  | { readonly loop?: undefined; readonly call: "eagle" | "elk"; readonly life: true };

/** A model's sound: a kolmila, a campfire or a water mill its loop, an eagle and an elk's head their calls. */
export function soundingOf(name: string): Sounding | null {
  const loop = SOURCES[familyOf(name)?.name ?? ""];
  if (loop) return { loop, life: false };
  if (name === "iso-eagle-1") return { call: "eagle", life: true };
  if (/^iso-elk-\d+-head$/.test(name)) return { call: "elk", life: true };
  return null;
}

/** A swarm of gnats, the nearest the middle: heard only closer up than a run starts. */
export function gnatLevel(q: number, near: number): number {
  return q * smoothstep(0.75, 0.95, near);
}

/** An animal's call, by how near the middle it is and how near the camera. */
export function callLevel(q: number, near: number): number {
  return q * smoothstep(0.3, 0.8, near);
}

/** What flies, an eagle or a flock: heard from further out, as the sky is seen from anywhere. */
export function skyLevel(q: number, near: number): number {
  return q * (0.3 + 0.7 * near);
}

/** How far into its file a fish's splash is, and a flock's first beat of its wings, in seconds. */
export const FISH_SPLASH_S = 0.09;
export const BIRDS_LEAD_S = 0.1;

/** The times start + k period from `from` up to `to`, with their rounds k, that `keep` keeps. */
function rounds(
  start: number,
  period: number,
  from: number,
  to: number,
  keep: (k: number) => boolean = () => true,
): Call[] {
  const out: Call[] = [];
  for (let k = Math.ceil((from - start) / period); start + k * period < to; k += 1) {
    if (keep(k)) out.push({ at: start + k * period, round: k });
  }
  return out;
}

/** A fish's leap in seconds: it falls back in this long after it leaves the water. */
export const FISH_LEAP = 0.7;

/**
 * When a fish falls back into the water between `from` and `to`: FISH_LEAP after each leap
 * begins, where t / period + phase is whole (visual spec, Markers, Fish).
 */
export function fishFalls(period: number, phase: number, from: number, to: number): Call[] {
  return rounds(FISH_LEAP - phase * period, period, from, to);
}

/**
 * When a duck at (x, y) starts to look about on every other round, the round it looks to one
 * side: at 0.52 of its round, as its dabble has it (visual spec, Scene composition, Riders).
 */
export function duckCalls(x: number, y: number, from: number, to: number): Call[] {
  const beat = fract(0.53 * x + 0.29 * y);
  const period = 20 + 6 * fract(0.17 * x + 0.41 * y);
  return rounds((0.52 - beat) * period, period, from, to, (k) => ((k % 2) + 2) % 2 === 0);
}

/** When an eagle soaring round (x, y) cries: once a circle, as its soar turns past a whole turn. */
export function eagleCries(x: number, y: number, from: number, to: number): Call[] {
  const speed = 0.31;
  const start = (-2 * Math.PI * fract(0.37 * x + 0.61 * y)) / speed;
  return rounds(start, (2 * Math.PI) / speed, from, to);
}

/**
 * When an elk whose neck is at (x, y) grunts: as it lifts its head to look about, at 0.6 of its
 * nod, every third time (visual spec, Scene composition, Riders).
 */
export function elkGrunts(x: number, y: number, from: number, to: number): Call[] {
  const beat = fract(0.37 * x + 0.61 * y);
  const period = 11 + 3 * fract(0.13 * x + 0.29 * y);
  return rounds((0.6 - beat) * period, period, from, to, (k) => ((k % 3) + 3) % 3 === 0);
}
